<template>
  <div class="view-container">
    <div class="view-gabarit">
      <VeeForm @submit="submitAddresses">
        <div class="view-row">
          <div class="view-description">
            <p class="baseline">{{ $t('fixAddressErrors') }}</p>
            <p class="description">{{ $t('fixAddressErrorsBaseline') }}</p>
            <div class="legend-container">
              <p class="legend-line"><i class="icon-checkstatus"></i>{{ $t('addressType.rewrite') }}</p>
              <p class="legend-line"><i class="icon-alert"></i>{{ $t('addressType.suggested') }}</p>
              <p class="legend-line"><i class="icon-zone"></i>{{ $t('addressType.outzone') }}</p>
            </div>
            <div class="counter-container">
              <div class="counter">
                <AnimatedValue class="active" :value="notAccountedAddressCount" />
                <span class="divider">/</span>
                <span class="total">{{ totalAddresses }}</span>
              </div>
              <div class="text">
                {{ $t('studyCreation.notAccountedAddress', notAccountedAddressCount) }}
              </div>
            </div>
            <button class="geocode-button" @click.prevent="refreshGeocoding">
              <i id="geocode-icon-refresh" class="icon-refresh"></i>
              <span>{{ $t('refreshGeocode') }}</span>
            </button>
          </div>
          <div class="view-content">
            <div class="block-addresses">
              <div class="title-expand" @click="() => toggleState('showInvalidAddresses')">
                {{ $t('NotRecognized') }} ({{ invalidAddresses.length }})
                <i v-if="invalidAddresses.length > 0" :class="[`icon-${showInvalidAddresses ? 'arrow_up' : 'arrow_down'}`]" />
              </div>
              <div v-if="showInvalidAddresses">
                <AddressInput
                  v-for="(address, idx) in invalidAddresses"
                  :key="idx"
                  prefix="invalid"
                  :address="address"
                  :idx="idx"
                  @updateAddress="updateNotAccountedAddress"
                />
              </div>
            </div>
            <div class="block-addresses">
              <div class="title-expand" @click="() => toggleState('showLevensteinAddresses')">
                {{ $t('Reformatted') }} ({{ levensteinAddresses.length }})
                <i v-if="levensteinAddresses.length > 0" :class="[`icon-${showLevensteinAddresses ? 'arrow_up' : 'arrow_down'}`]" />
              </div>
              <div v-if="showLevensteinAddresses">
                <AddressInput
                  v-for="(address, idx) in levensteinAddresses"
                  :key="idx"
                  prefix="levenstein"
                  :address="address"
                  :idx="idx"
                  @updateAddress="updateNotAccountedAddress"
                />
              </div>
            </div>
            <div class="block-addresses">
              <div class="title-expand" @click="() => toggleState('showValidAddresses')">
                {{ $t('Valid') }} ({{ perfectAddresses.length }})
                <i v-if="perfectAddresses.length > 0" :class="[`icon-${showValidAddresses ? 'arrow_up' : 'arrow_down'}`]" />
              </div>
              <div v-if="showValidAddresses">
                <fieldset v-for="(address, idx) in perfectAddresses" :key="idx">
                  <label :for="`input-perfect-${address.id}`">{{ getAddressLabel(address) }}</label>
                  <div class="input-gabarit">
                    <input :id="`input-perfect-${address.id}`" type="text" disabled :value="address.formatted" />
                    <span class="flag-box">
                      <i v-if="address.levenstein >= 8" class="icon-alert"></i>
                      <i v-if="address.isExcluded" class="icon-zone"></i>
                    </span>
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
        </div>
        <div class="view-row">
          <div class="view-description"></div>
          <div class="view-content">
            <fieldset>
              <button class="study-button main" type="submit">{{ $t('validate') }}</button>
            </fieldset>
          </div>
        </div>
      </VeeForm>
    </div>
  </div>
</template>

<script>
import AnimatedValue from '@/components/AnimatedValue';
import AddressInput from '@/components/Study/AddressInput';
import { Form as VeeForm } from 'vee-validate';

export default {
  components: {
    VeeForm,
    AnimatedValue,
    AddressInput,
  },

  data() {
    return {
      rawAddresses: [],
      addresses: [],
      suggestions: [],
      googleSuggested: {},
      showInvalidAddresses: true,
      showLevensteinAddresses: false,
      showValidAddresses: false,
      fetching: false,
      status: null,
      code: null,
      notAccountedAddressCount: 0,
    };
  },

  computed: {
    totalAddresses() {
      return this.rawAddresses.length;
    },

    invalidAddresses() {
      return this.rawAddresses.filter(item => item.address.status === 0 || item.address.status === 3 || item.address.status === 4).map(this.mapAddress);
    },

    levensteinAddresses() {
      return this.rawAddresses.filter(item => item.address.status === 2 && item.address.levenstein >= 8).map(this.mapAddress);
    },

    perfectAddresses() {
      return this.rawAddresses.filter(item => item.address.status === 1 || (item.address.status === 2 && item.address.levenstein < 8)).map(this.mapAddress);
    },
  },

  watch: {
    rawAddresses() {
      const address = [...this.invalidAddresses, ...this.levensteinAddresses];
      const emptyAddresses = address.map(a => a.formatted).filter(a => a === '' || !a);

      this.notAccountedAddressCount = emptyAddresses.length;
    },
  },

  mounted() {
    this.gMaps = google.maps;
    this.sessionToken = new this.gMaps.places.AutocompleteSessionToken();
    this.fetching = true;
    this.fetchProcess();
  },

  beforeUnmount() {
    if (this.setTimeout) {
      clearTimeout(this.setTimeout);
    }
  },

  methods: {
    getAddressLabel(address) {
      if (address.coordinatesProvided) {
        return `${address.jllId} : ${address.latlong.latitude}, ${address.latlong.longitude}`;
      }

      // first input is checked on length as sometimes it just contains a single coma
      if (address.firstInput.trim().length > 1) {
        return `${address.jllId} : ${address.firstInput}`;
      }

      return `${address.jllId}`;
    },

    updateNotAccountedAddress({ value, oldValue }) {
      if (value !== '' && oldValue !== '') return;

      const address = [...this.invalidAddresses, ...this.levensteinAddresses];
      const emptyAddresses = address.map(a => a.formatted).filter(a => a === '' || !a);

      this.notAccountedAddressCount = emptyAddresses.length;
    },

    toggleState(state) {
      this[state] = !this[state];
    },

    fetchProcess() {
      if (this._isBeingDestroyed || this._isDestroyed) {
        if (this.setTimeout) {
          clearTimeout(this.setTimeout);
        }
        return;
      }
      const id = this.$route.params.studyId;
      if (id) {
        this.$store
          .dispatch('fetchProcessStatus', {
            id,
          })
          .then(response => {
            if (this._isBeingDestroyed || this._isDestroyed) {
              if (this.setTimeout) {
                clearTimeout(this.setTimeout);
              }
              return;
            }
            const { code, value } = response;
            this.status = value;
            this.code = code;
            if (code === 1) {
              // goBackToCsv
              console.warn('goTo CSV upload');
              this.$router.replace({ name: 'studyUploadCSV' });
            } else if (code === 2) {
              // CSV updatloaded GoTO ajoutFIlters
              console.warn('goTo udpdate filters');
              this.$router.replace({ name: 'studyUpdateFilters' });
            } else if (code === 3) {
              // filterOK goTO updateSites
              console.warn('goTo udpdate sites');
              this.$router.replace({ name: 'studyCurrentSites' });
            } else if (code === 4 || code === 5) {
              // sitesCreated go to address checking
              // WAITING for or 5 isGEOCODING code 6 ou 7
              if (this._isBeingDestroyed || this._isDestroyed) {
                clearTimeout(this.setTimeout);
                return;
              }
              this.setTimeout = setTimeout(() => this.fetchProcess(), 1000);
            } else if (code === 6 || code === 7 || code === 9 || code === 16) {
              // ou moins une adress en warning
              this.getCollaborators(id);
            } else if (code === 8) {
              // coverage Error
            } else {
              console.warn('excess code error', code);
              alert('Une erreur est survenue');
              this.$router.push({ name: 'studyList' });
            }
          });
      }
    },

    mapAddress(item) {
      this.googleSuggested[item.address.id] = true;
      return {
        jllId: item.jll_identifier,
        firstInput: item.address.first_input_ever,
        originalFormatted: item.address.formatted,
        formatted: item.address.formatted,
        levenstein: item.address.levenstein,
        isExcluded: item.address.is_excluded,
        coordinatesProvided: item.address.provided_coordinates,
        status: item.address.status,
        input: item.address.input,
        id: item.address.id,
        latlong: {
          latitude: item.address.lattitude,
          longitude: item.address.longitude,
        },
      };
    },

    getCollaborators(id) {
      this.$store
        .dispatch('fetchCollaborators', {
          id,
        })
        .then(response => {
          this.fetching = false;
          this.rawAddresses = response;
          this.updateNotAccountedAddress({ value: '', oldValue: '' });
        });
    },

    async checkAddresses() {
      const addresses = [];
      this.invalidAddresses.forEach(address => {
        if (address.formatted !== address.input) {
          const { id } = address;
          addresses.push({
            id,
            input: address.formatted,
          });
        }
      });
      this.levensteinAddresses.forEach(address => {
        if (address.formatted !== address.input) {
          const { id } = address;
          addresses.push({
            id,
            input: address.formatted,
          });
        }
      });
      await this.$store.dispatch('putAddresses', { addresses });
    },

    async submitAddresses() {
      if (this.invalidAddresses.length > 0 || this.levensteinAddresses.length > 0) {
        await this.checkAddresses();
        this.checkNextStep();
      } else {
        this.$router.push({ name: 'studyEditRegions' });
      }
    },

    checkNextStep() {
      clearTimeout(this.setTimeout);

      this.fetching = true;
      const id = this.$route.params.studyId;
      this.$store.dispatch('fetchProcessStatus', { id }).then(response => {
        if ([6, 7].includes(response.code)) {
          this.fetching = false;

          this.$router.push({ name: 'studyEditRegions' });
        } else {
          this.setTimeout = setTimeout(() => this.checkNextStep(), 1000);
        }
      });
    },

    async refreshGeocoding() {
      const id = this.$route.params.studyId;
      document.getElementById('geocode-icon-refresh').animate([{ transform: 'rotate(0)' }, { transform: 'rotate(-360deg)' }], 500);

      if (this.invalidAddresses.length > 0 || this.levensteinAddresses.length > 0) {
        await this.checkAddresses();
      }

      this.$store
        .dispatch('geocode', { id })
        .then(setTimeout(() => this.fetchProcess(), 1000))
        .catch(error => console.error(error));
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';

.counter-container {
  height: 80px;
  padding: 8px 16px;
  margin: 24px 0 0;
  border: solid 1px colors.$clear-border-color;
  border-right: 0;
  border-left: 0;
  display: flex;
  align-items: center;

  .counter {
    flex-shrink: 0;
    font-size: 48px;
    margin-right: 32px;
    font-family: 'Golden Plains';

    .active {
      display: inline-block;
      width: 64px;
      text-align: right;
      color: colors.$main-color;
    }
    .divider {
      color: colors.$earth-color;
      margin: 0 8px;
    }
    .total {
      color: colors.$dark-color;
    }
  }

  .text {
  }
}

.geocode-button {
  display: flex;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 14px;

  color: colors.$main-color;
  border: none;
  background: none;
  text-decoration: underline;
  justify-content: flex-end;
  text-align: right;
  padding: 16px 0 16px 24px;
  margin-left: auto;

  &:hover {
    opacity: 0.6;
  }
}

.block-addresses {
  max-width: 370px;
  margin-left: auto;
  margin-right: auto;
  & + .block-addresses {
    border-top: 1px solid #e3e3e3;
  }
}
.title-expand {
  font-size: 1.25em;
  font-weight: 600;
  cursor: pointer;
  line-height: 2.5;
}
.input-gabarit {
  position: relative;
}
.flag-box {
  position: absolute;
  white-space: nowrap;
  i {
    font-size: 2em;
    color: #bc141a;
  }
}
.autocomplete-dropdown {
  margin-top: -17px;
  margin-bottom: 16px;
  border: 1px solid #e3e3e3;
  margin-bottom: 16px;
  .autocomplete-entry {
    line-height: 2;
    padding: 0 0.5em;
    cursor: pointer;
    & + .autocomplete-entry {
      border-top: 1px solid #e3e3e3;
    }
  }
}
label {
  padding-bottom: 4px;
  overflow: visible;
}
.warning-tag {
  background-color: #bc141a;
  font-size: 11px;
  color: white;
  border-radius: 2px;
  padding-left: 7px;
  padding-right: 7px;
}

.legend-container {
  border: solid 1px #e3e3e3;
  border-left: 0;
  border-right: 0;
  padding: 8px 8px;
  margin: 32px 0 0;

  .legend-line {
    margin: -4px 0;
    display: flex;
    align-items: center;

    i {
      font-size: 2em;

      &.icon-checkstatus {
        color: #1daea1;
        font-size: 2.4em;
        margin: 0 -4px;
      }
      &.icon-alert {
        color: #bc141a;
      }
    }
  }
}
</style>
